import { BOARD_TOTAL_MINUTES, HOUR_WIDTH } from './geometry';

/**
 * Pixels → time, the inverse of `geometry`'s time → pixels. Pure, no React.
 *
 * Everything here works in **deltas**, never absolute clock times: a drag knows only how far the
 * pointer moved. Applying that delta to the entry's own ISO timestamps sidesteps timezone
 * arithmetic entirely — there is no local-midnight to reconstruct and no DST edge to get wrong.
 */

/** A boundary between two segments of a segmented block (travel | work | departure). */
export type SegmentHandle = 'travel' | 'work' | 'departure';

/** An edge of a plain span — the one bar an absence has. */
export type SpanHandle = 'start' | 'end';

/** Everything a drag can grab: the block body, a segment boundary, or a span edge. */
export type DragHandle = 'move' | SegmentHandle | SpanHandle;

/** Grid the drag snaps to. Matches `useCalendarBlockDrag`'s 15-minute rounding. */
export const SNAP_MINUTES = 15;

/** Pointer travel before a press becomes a drag — below this it stays a click. */
export const DRAG_THRESHOLD_PX = 10;

/**
 * Hold, before a **touch** gesture is allowed to become a drag. Below this the calendar scrolls
 * like everywhere else — see `useCalendarBlockDrag`'s "armed" gate. A mouse skips this entirely.
 */
export const TOUCH_HOLD_MS = 450;

/** Finger travel that cancels the hold — that is a scroll starting, not a hold. */
export const TOUCH_HOLD_TOLERANCE_PX = 8;

/** `SlotSchema` rejects a non-positive occupancy, so work can never be dragged to nothing. */
export const MIN_WORK_MINUTES = 15;

/** A horizontal pixel distance as minutes. */
export function xPxToMinutes(px: number): number {
  return (px / HOUR_WIDTH) * 60;
}

export function snapMinutes(minutes: number, step: number = SNAP_MINUTES): number {
  return Math.round(minutes / step) * step;
}

/**
 * Limits a move so the block cannot be dragged off the drawn window.
 *
 * A block already clipped at an edge keeps whatever room it has on the other side — clamping it to
 * the visible span would yank it inward the moment it was touched.
 */
export function clampMoveDelta(
  deltaMinutes: number,
  span: { startMin: number; endMin: number },
): number {
  const min = -span.startMin;
  const max = BOARD_TOTAL_MINUTES - span.endMin;
  return Math.max(min, Math.min(deltaMinutes, max));
}

/** Shifts an ISO instant by whole minutes, preserving everything else about it. */
export function shiftIso(iso: string, deltaMinutes: number): string {
  return new Date(new Date(iso).getTime() + deltaMinutes * 60_000).toISOString();
}

export interface MinuteRange {
  startMin: number;
  endMin: number;
}

/** The three occupancy components of an appointment or a task. */
export interface Durations {
  travel: number;
  work: number;
  departure: number;
}

export interface DraggedDurations {
  durations: Durations;
  /** Minutes the block's start edge moves. */
  startDelta: number;
  /** Minutes the work start moves — what a patch writes. */
  workStartDelta: number;
}

/**
 * What one handle's delta does to the three durations. **The one drag rule** — both the preview and
 * the request that commits it are derived from this, so the ghost cannot promise something the
 * patch will not do.
 *
 * There used to be two: `previewRange` here and `applyDrag` in `buildSchedulePatch`, whose docblock
 * claimed to mirror it. It did not — `applyDrag` clamped each component and `previewRange` clamped
 * nothing, so dragging the left edge past the Anfahrt's own length kept eating into the work segment
 * on screen while the commit had already stopped.
 *
 * Each handle owns exactly one field, which is what makes a resize predictable:
 *
 *   left edge          → travel   (work start does not move)
 *   work|dep divider   → work
 *   right edge         → departure
 *   body               → work start (all three durations unchanged)
 */
export function applyDurationDrag(
  base: Durations,
  handle: 'move' | SegmentHandle,
  deltaMinutes: number,
): DraggedDurations {
  const d = deltaMinutes;

  switch (handle) {
    case 'move':
      return { durations: base, startDelta: d, workStartDelta: d };

    case 'travel': {
      // Dragging the left edge leftwards (negative delta) lengthens the Anfahrt.
      const travel = Math.max(0, base.travel - d);
      // Once travel hits 0 the edge stops, so the start moves by what travel actually gave up.
      return {
        durations: { ...base, travel },
        startDelta: base.travel - travel,
        workStartDelta: 0,
      };
    }

    case 'work':
      return {
        durations: { ...base, work: Math.max(MIN_WORK_MINUTES, base.work + d) },
        startDelta: 0,
        workStartDelta: 0,
      };

    case 'departure':
      return {
        durations: { ...base, departure: Math.max(0, base.departure + d) },
        startDelta: 0,
        workStartDelta: 0,
      };

    default:
      return { durations: base, startDelta: 0, workStartDelta: 0 };
  }
}
